import React, { KeyboardEvent } from "react";
import {
  Facebook,
  Linkedin,
  TwitterX,
  Whatsapp
} from "@yleisradio/yds-icons-react";
import styled, { css } from "styled-components";

import { UI_SIZE } from "../../enums";
import { trackShareEvent } from "../../helpers/analyticsHelpers";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { buttonStyle, fontFamily } from "../../styles/global";
import { focusColor } from "../../styles/variables";
import { EmbedIcon, LinkIcon } from "../Icons";
import { stopEventPropagation } from "../player-ui/helpers";

interface ResponsiveProps {
  uiSize: string;
}

interface CommonParams extends ResponsiveProps {
  title: string;
  children?: React.ReactNode;
  ariaLabel: string;
}

interface ShareButtonParams extends CommonParams {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onKeydown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

interface ShareLinkParams extends CommonParams {
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  onKeydown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  href: string;
}

const ShareSocialMediaLinks = styled.div<ResponsiveProps>`
  align-items: center;
  display: flex;
  max-width: 100%;
  margin-bottom: ${props => (props.uiSize === UI_SIZE.DEFAULT ? "16px" : "0")};
  overflow: hidden;
  overflow-x: auto;
  padding: 2px 0;
`;

const shareLinkOrButtonStyle = css<ResponsiveProps>`
  &&& {
    box-sizing: initial;
    color: inherit;
    min-width: 60px;
    opacity: 0.8;
    text-align: center;
    text-decoration: none;

    &:focus-visible {
      outline: 1px solid ${focusColor};
    }

    &:first-child {
      margin-left: 10px;
    }

    &:last-child {
      margin-right: 10px;
    }

    svg {
      height: 50px;
      margin-bottom: 5px;
      width: 50px;
    }

    ${props => {
      if (props.uiSize === UI_SIZE.DEFAULT) {
        return css`
          border: 0 none;
          min-width: 5rem;

          /* stylelint-disable-next-line no-descending-specificity */

          svg {
            /* TODO: Icon size should be detected somehow with js, maybe with styled-components */
            height: 60px;
            width: 60px;
          }

          &:hover {
            opacity: 1;
          }
        `;
      }
    }}
  }
`;

const ShareLinkA = styled.a<ResponsiveProps>`
  ${shareLinkOrButtonStyle}
`;

const ShareLinkLabel = styled.div<ResponsiveProps>`
  display: ${props =>
    props.uiSize === UI_SIZE.SMALL || props.uiSize === UI_SIZE.XSMALL
      ? "none"
      : "block"};
  ${fontFamily};
  font-size: 16px;
  line-height: 16px;
`;

const ShareLinkWithLabel = ({
  ariaLabel,
  children,
  href,
  onClick,
  title,
  uiSize
}: ShareLinkParams) => (
  <ShareLinkA
    aria-label={ariaLabel}
    target={href === "#!" ? "_self" : "_blank"}
    rel="noopener noreferrer"
    href={href}
    onClick={onClick}
    uiSize={uiSize}>
    {children}
    <ShareLinkLabel uiSize={uiSize}>{title}</ShareLinkLabel>
  </ShareLinkA>
);

const ShareButton = styled.button<ResponsiveProps>`
  ${buttonStyle}
  ${shareLinkOrButtonStyle}
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ShareButtonWithLabel = ({
  ariaLabel,
  children,
  onClick,
  onKeydown,
  title,
  uiSize
}: ShareButtonParams) => (
  <ShareButton
    aria-label={ariaLabel}
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === " ") {
        onKeydown?.(e);
      }
    }}
    onKeyUp={stopEventPropagation}
    uiSize={uiSize}>
    {children}
    <ShareLinkLabel uiSize={uiSize}>{title}</ShareLinkLabel>
  </ShareButton>
);

interface GetShareUrlInterface {
  utmSource: string;
  url: string;
  shareFromInSeconds: number;
}

interface Props {
  shareFromInSeconds: number;
  language: string;
  getShareUrl: (props: GetShareUrlInterface) => string;
  isContentProtected: boolean;
  toggleEmbedCode: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
  toggleLink: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
  uiSize: UI_SIZE;
  jaaOhjelma: string;
}

const SocialMediaLinks = ({
  getShareUrl,
  isContentProtected,
  jaaOhjelma,
  language,
  shareFromInSeconds,
  toggleEmbedCode,
  toggleLink,
  uiSize
}: Props) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  return (
    <ShareSocialMediaLinks uiSize={uiSize}>
      <ShareLinkWithLabel
        title="Facebook"
        ariaLabel={`${jaaOhjelma} Facebook`}
        href={getShareUrl({
          shareFromInSeconds,
          url: "https://www.facebook.com/sharer/sharer.php?u=",
          utmSource: "facebook-share"
        })}
        onClick={() =>
          trackShareEvent("areena.share-buttons.facebook", { language })
        }
        uiSize={uiSize}>
        <Facebook />
      </ShareLinkWithLabel>
      <ShareLinkWithLabel
        title="X"
        ariaLabel={`${jaaOhjelma} X`}
        href={getShareUrl({
          shareFromInSeconds,
          url: "https://twitter.com/intent/tweet?url=",
          utmSource: "twitter-share"
        })}
        onClick={() =>
          trackShareEvent("areena.share-buttons.twitter", { language })
        }
        uiSize={uiSize}>
        <TwitterX />
      </ShareLinkWithLabel>
      <ShareLinkWithLabel
        title="LinkedIn"
        ariaLabel={`${jaaOhjelma} LinkedIn`}
        href={getShareUrl({
          shareFromInSeconds,
          url: "https://www.linkedin.com/shareArticle?mini=true&url=",
          utmSource: "linkedin-share"
        })}
        onClick={() =>
          trackShareEvent("areena.share-buttons.linkedin", { language })
        }
        uiSize={uiSize}>
        <Linkedin />
      </ShareLinkWithLabel>
      {device.isMobile && (
        <ShareLinkWithLabel
          title="WhatsApp"
          ariaLabel={`${jaaOhjelma} WhatsApp`}
          href={getShareUrl({
            shareFromInSeconds,
            url: "https://wa.me/?text=",
            utmSource: "whatsapp-share"
          })}
          onClick={() =>
            trackShareEvent("areena.share-buttons.whatsapp", {
              language
            })
          }
          uiSize={uiSize}>
          <Whatsapp />
        </ShareLinkWithLabel>
      )}
      {!isContentProtected && (
        <ShareButtonWithLabel
          title={i18n("upota", { language })}
          ariaLabel={i18n("kopioi_upotuskoodi", { language })}
          onClick={toggleEmbedCode}
          onKeydown={toggleEmbedCode}
          uiSize={uiSize}>
          <EmbedIcon color="white" />
        </ShareButtonWithLabel>
      )}
      <ShareButtonWithLabel
        title={i18n("linkki", { language })}
        ariaLabel={i18n("kopioi_ohjelmasivun_linkki", { language })}
        onClick={toggleLink}
        onKeydown={toggleLink}
        uiSize={uiSize}>
        <LinkIcon color="white" />
      </ShareButtonWithLabel>
    </ShareSocialMediaLinks>
  );
};

export default SocialMediaLinks;
